import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAngularContext } from 'AngularContext';
import { type DialogModal } from 'DialogModal';
import { AdmissionOfferStatus } from 'AdmissionOffer';
import { getSupportsAcceptanceMessageOnApplicationPage } from 'Cohorts';
import { type CurrentUser, getAoiRecord, getCohort, userActionsApi } from 'Users';
import { selectInGhostMode } from 'Users/ghostModeSlice';

/*
    Shows the full-screen acceptance message once, when the user first sees an offer of admission.

    `<application-accepted>` is still an Angular directive (Lessons/.../stream/accepted_application_dir.js),
    so it is rendered through DialogModal rather than as a React component.
    See https://trello.com/c/4pcyVnov.
*/
export function useAcceptedApplicationModal(currentUser: CurrentUser): void {
    const $injector = useAngularContext();
    const inGhostMode = useSelector(selectInGhostMode);
    const [updateCurrentUser] = userActionsApi.useUpdateCurrentUserMutation();

    const hasShownModal = useRef(false);

    const cohort = getCohort(currentUser);
    const status = getAoiRecord(currentUser)?.status;
    const supportsAcceptanceMessage = !!cohort && getSupportsAcceptanceMessageOnApplicationPage(cohort);
    const shouldShow =
        status === AdmissionOfferStatus.OfferedAdmission && !currentUser.hasSeenAccepted && supportsAcceptanceMessage;

    useEffect(() => {
        if (!shouldShow || hasShownModal.current || !$injector) return;
        hasShownModal.current = true;

        const dialogModal = $injector.get<DialogModal>('DialogModal');
        dialogModal.alert({
            content: '<application-accepted></application-accepted>',
            size: 'fullscreen',
            hideCloseButton: true,
            closeOnClick: false,
            classes: ['accepted-application-modal'],
        });

        if (!inGhostMode) {
            updateCurrentUser({ user: { id: currentUser.id, has_seen_accepted: true } });
        }
    }, [shouldShow, $injector, inGhostMode, updateCurrentUser, currentUser.id]);

    useEffect(
        () => () => {
            $injector?.get<DialogModal>('DialogModal').hideAlerts();
        },
        [$injector],
    );
}
